import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent2 as accent, slide2HighlightSanskrit, useSlide2, Seg } from "./slide2.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 2 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide2.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 6px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.6, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#e8e6ff", fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide2Mobile: React.FC = () => {
  const { meta: slide2Meta, states: slide2States, aum: slide2Aum, inspiration: slide2Inspiration, connection: slide2Connection } = useSlide2();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#7a72b8", textTransform: "uppercase", marginBottom: 8 }}>
        {slide2Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide2Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 27, lineHeight: 1.18, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide2Meta.titlePlain} <span style={{ color: accent }}>{slide2Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#8a86c0", margin: "0 0 20px" }}>
        {slide2Meta.subtitle}
      </p>

      {/* inspiration */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide2Meta.inspirationLabel}</div>
        <p style={txt}>{renderSegs(slide2Inspiration)}</p>
      </div>

      {/* AUM */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide2Meta.aumLabel} <span style={{ opacity: 0.55, textTransform: "none", letterSpacing: 1 }}>· {slide2Meta.aumSub}</span></div>
        <div style={{ display: "flex", gap: 14, marginBottom: 10 }}>
          {slide2Aum.map((a, i) => (
            <div key={i} style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: 24, fontWeight: 600, color: i === 1 ? accent : i === 3 ? "rgba(127,119,221,0.6)" : "rgba(127,119,221,0.42)", fontFamily: FONT_DISPLAY, marginBottom: 3 }}>{a.letter}</div>
              <div style={{ fontSize: 9, fontFamily: mono, color: "#6a6a90", letterSpacing: 0.5 }}>{a.meaning}</div>
            </div>
          ))}
        </div>
        <p style={{ ...txt, color: "#9bbdd6", fontSize: 14 }}>{slide2Meta.aumNote}</p>
      </div>

      {/* four states */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide2Meta.statesLabel}</div>
        {slide2States.map((s, i) => {
          const hl = s.sanskrit === slide2HighlightSanskrit;
          return (
            <div key={i} style={{
              background: hl ? "rgba(127,119,221,0.12)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${hl ? "rgba(127,119,221,0.4)" : "rgba(127,119,221,0.12)"}`,
              borderRadius: 9, padding: "11px 13px", marginBottom: 8, position: "relative",
            }}>
              <div style={{ position: "absolute", top: 8, right: 12, fontSize: 22, color: hl ? "rgba(127,119,221,0.8)" : "rgba(127,119,221,0.28)", fontFamily: FONT_DISPLAY, fontWeight: 600 }}>{s.symbol}</div>
              <div style={{ fontSize: 9, fontFamily: mono, color: accent, letterSpacing: 2, marginBottom: 2 }}>{s.sanskrit}</div>
              <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: "#e0eeff", marginBottom: 3 }}>{s.english}</div>
              <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.5, color: hl ? "#c8c4f8" : "#9bbdd6", margin: 0, paddingRight: 24 }}>{s.description}</p>
            </div>
          );
        })}
      </div>

      {/* connection callout */}
      <div style={{ background: "rgba(127,119,221,0.06)", border: "1px solid rgba(127,119,221,0.20)", borderRadius: 10, padding: "13px 15px" }}>
        <div style={lbl}>{slide2Meta.connectionLabel}</div>
        <p style={{ ...txt, color: "#d0ccff" }}>{renderSegs(slide2Connection)}</p>
      </div>
    </div>
  );
};

export default HTLDSlide2Mobile;
